import type { SponsorDto, SponsorRequestCheckDto, SponsorRequestDto, SponsorshipDto } from '@ukinfo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { SPONSORSHIP_STATUS_LABELS, SPONSOR_STATUS_LABELS, formatDate } from '../lib/format';
import { useConfirm } from '../components/ConfirmDialog';
import { Amount } from '../components/Amount';
import {
  FURTHER_REQUEST_LABELS,
  FurtherRequestsBadge,
  SponsorLogo,
} from '../components/sponsoring/common';
import { RequestDialog } from '../components/sponsoring/RequestDialog';
import { RequestList } from '../components/sponsoring/RequestList';
import { SponsorContacts } from '../components/sponsoring/SponsorContacts';
import { SponsorDialog } from '../components/sponsoring/SponsorDialog';
import { ContractDialog } from './SponsoringPage';
import {
  Badge,
  Button,
  Card,
  CardBody,
  ErrorBlock,
  LoadingBlock,
  SectionTitle,
  cn,
  useToast,
} from '../components/ui';

/**
 * Alles zu einem Sponsor auf einer Seite.
 *
 * Oben steht, was vor einer Anfrage wichtig ist: wer betreut, ob und wofuer
 * angefragt werden darf und was zuletzt passiert ist. Vertragsinhalte folgen
 * nur fuer Personen mit Vertragsrecht.
 */
export function SponsorProfilePage(): JSX.Element {
  const { sponsorId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [contractOpen, setContractOpen] = useState(false);

  const sponsor = useQuery({
    queryKey: ['sponsors', 'detail', sponsorId],
    queryFn: () => api.get<SponsorDto>(`/api/sponsors/${sponsorId}`),
    enabled: Boolean(sponsorId),
  });
  const requests = useQuery({
    queryKey: ['sponsor-requests', 'sponsor', sponsorId],
    queryFn: () => api.get<SponsorRequestDto[]>('/api/sponsor-requests', { sponsorId }),
    enabled: Boolean(sponsorId),
  });
  const check = useQuery({
    queryKey: ['sponsor-request-check', sponsorId],
    queryFn: () => api.get<SponsorRequestCheckDto>(`/api/sponsors/${sponsorId}/request-check`),
    enabled: Boolean(sponsorId),
  });
  const mayContracts = can('sponsoring_contracts', 'read');
  const contracts = useQuery({
    queryKey: ['sponsorships', 'sponsor', sponsorId],
    queryFn: () => api.get<SponsorshipDto[]>('/api/sponsorships', { sponsorId, includeChildren: true }),
    enabled: Boolean(sponsorId) && mayContracts,
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['sponsors'] });
  };

  const uploadLogo = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return api.upload<SponsorDto>(`/api/sponsors/${sponsorId}/logo`, form);
    },
    onSuccess: async () => {
      await refresh();
      toast.show('Logo gespeichert');
    },
    onError: (err: unknown) => toast.show(err instanceof Error ? err.message : 'Logo konnte nicht gespeichert werden', 'error'),
    onSettled: () => {
      if (fileRef.current) fileRef.current.value = '';
    },
  });

  const { confirm, confirmDialog } = useConfirm();

  const removeLogo = useMutation({
    mutationFn: () => api.del(`/api/sponsors/${sponsorId}/logo`),
    onSuccess: async () => {
      await refresh();
      toast.show('Logo entfernt');
    },
    onError: (err: unknown) => toast.show(err instanceof ApiError ? err.message : 'Entfernen fehlgeschlagen', 'error'),
  });

  const removeSponsor = useMutation({
    mutationFn: () => api.del(`/api/sponsors/${sponsorId}`),
    onSuccess: async () => {
      await refresh();
      toast.show('Sponsor entfernt');
      navigate('/sponsoring');
    },
    onError: (err: unknown) => toast.show(err instanceof ApiError ? err.message : 'Löschen fehlgeschlagen', 'error'),
  });

  if (sponsor.isLoading) return <LoadingBlock />;
  if (sponsor.error) return <ErrorBlock error={sponsor.error} />;
  if (!sponsor.data) return <ErrorBlock error={new Error('Sponsor nicht gefunden')} />;

  const s = sponsor.data;
  const mayEdit = can('sponsoring', 'update');
  const mayRequest = can('sponsoring_requests', 'create');
  const blocked = check.data?.blocked ?? false;
  const ampel = {
    OPEN: 'border-emerald-200 bg-emerald-50',
    VIA_MANAGER: 'border-amber-200 bg-amber-50',
    NO: 'border-rose-200 bg-rose-50',
  }[s.furtherRequests];

  return (
    <div className="space-y-4">
      {confirmDialog}
      <Link to="/sponsoring" className="text-sm text-brand-700 hover:underline">← Zurück zum Sponsoring</Link>

      <Card>
        <CardBody className="space-y-4">
          <div className="flex flex-wrap items-start gap-4">
            <div className="flex flex-col items-center gap-1">
              <SponsorLogo name={s.companyName} url={s.logoUrl} size="lg" />
              {mayEdit && (
                <div className="flex gap-2 text-xs">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    className="hidden"
                    aria-label="Logo hochladen"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) uploadLogo.mutate(file);
                    }}
                  />
                  <button
                    type="button"
                    className="font-medium text-brand-700 hover:underline disabled:opacity-50"
                    disabled={uploadLogo.isPending}
                    onClick={() => fileRef.current?.click()}
                  >
                    {s.logoUrl ? 'Logo ändern' : 'Logo hochladen'}
                  </button>
                  {s.logoUrl && (
                    <button
                      type="button"
                      className="text-slate-400 hover:text-rose-600"
                      onClick={async () => {
                        const ok = await confirm({
                          title: 'Logo entfernen?',
                          message: `Das Logo von ${s.companyName} wird gelöscht. Statt des Logos erscheinen die Initialen.`,
                          confirmLabel: 'Ja, entfernen',
                          tone: 'danger',
                        });
                        if (ok) removeLogo.mutate();
                      }}
                    >
                      entfernen
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1 space-y-2">
              <h1 className="text-xl font-semibold text-slate-900">{s.companyName}</h1>
              <div className="flex flex-wrap gap-1">
                {s.categoryName && <Badge tone="brand">{s.categoryName}</Badge>}
                <Badge tone={s.status === 'ACTIVE' ? 'success' : 'neutral'}>
                  {SPONSOR_STATUS_LABELS[s.status] ?? s.status}
                </Badge>
              </div>
              {s.website && (
                <a href={s.website} target="_blank" rel="noreferrer noopener" className="block truncate text-sm text-brand-700 hover:underline">
                  {s.website}
                </a>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {mayEdit && <Button variant="secondary" size="sm" onClick={() => setEditOpen(true)}>Bearbeiten</Button>}
              {can('sponsoring', 'delete') && s.activeSponsorships === 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (window.confirm(`Sponsor „${s.companyName}“ entfernen?`)) removeSponsor.mutate();
                  }}
                >
                  Entfernen
                </Button>
              )}
            </div>
          </div>

          {/* Bevor du anfragst */}
          <div className={cn('space-y-2 rounded-xl border p-4', ampel)}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-semibold text-slate-900">Bevor du anfragst</h2>
              <FurtherRequestsBadge value={s.furtherRequests} />
            </div>
            <dl className="grid gap-2 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-500">Betreut von</dt>
                <dd className="text-slate-800">
                  {s.managedByPersonId ? (
                    <Link to={`/personen/${s.managedByPersonId}`} className="hover:underline">{s.managedByName}</Link>
                  ) : 'Noch niemand eingetragen'}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-500">Anfragen</dt>
                <dd className="text-slate-800">{FURTHER_REQUEST_LABELS[s.furtherRequests]}</dd>
              </div>
              {s.interestOffers.length > 0 && (
                <div>
                  <dt className="text-xs uppercase tracking-wide text-slate-500">Ansprechbar für</dt>
                  <dd className="flex flex-wrap gap-1 pt-0.5">
                    {s.interestOffers.map((o) => <Badge key={o.id} tone="info">{o.title}</Badge>)}
                  </dd>
                </div>
              )}
              {s.openFor && (
                <div>
                  <dt className="text-xs uppercase tracking-wide text-slate-500">Hinweise</dt>
                  <dd className="text-slate-800">{s.openFor}</dd>
                </div>
              )}
            </dl>
            {check.data && check.data.hints.length > 0 && (
              <ul className="list-disc space-y-0.5 pl-5 text-sm text-slate-700">
                {check.data.hints.map((hint) => <li key={hint}>{hint}</li>)}
              </ul>
            )}
            {mayRequest && (
              <div className="pt-1">
                <Button size="sm" disabled={blocked} onClick={() => setRequestOpen(true)}>
                  Anfrage erfassen
                </Button>
                {blocked && <span className="ml-2 text-xs text-rose-700">Nur die Betreuung kann hier anfragen.</span>}
              </div>
            )}
          </div>

          {s.supportedUnits.length > 0 && (
            <div>
              <h2 className="mb-1 text-sm font-semibold text-slate-700">Unterstützt</h2>
              <ul className="flex flex-wrap gap-2 text-sm">
                {s.supportedUnits.map((unit) => (
                  <li key={unit.id} className="rounded-lg bg-slate-50 px-2 py-1">
                    {unit.name} <span className="text-slate-500">bis {formatDate(unit.validUntil)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <SectionTitle
            title="Anfrageverlauf"
            subtitle="Wer hat wann wofür angefragt"
            action={mayRequest && !blocked ? (
              <Button size="sm" variant="secondary" onClick={() => setRequestOpen(true)}>+ Anfrage</Button>
            ) : undefined}
          />
          {requests.isLoading && <LoadingBlock />}
          {requests.data && <RequestList requests={requests.data} showSponsor={false} />}
        </CardBody>
      </Card>

      {s.detailsVisible && (
        <Card>
          <CardBody className="space-y-3">
            <SectionTitle title="🔒 Kontakte & Notizen" subtitle="Nur mit Zugriff auf Sponsoringverträge sichtbar" />
            <SponsorContacts sponsor={s} />
            {s.notes && <p className="whitespace-pre-wrap text-sm text-slate-600">{s.notes}</p>}
          </CardBody>
        </Card>
      )}

      {mayContracts && (
        <Card>
          <CardBody>
            <SectionTitle
              title="🔒 Verträge"
              action={can('sponsoring_contracts', 'create') ? (
                <Button size="sm" variant="secondary" onClick={() => setContractOpen(true)}>+ Vertrag</Button>
              ) : undefined}
            />
            {contracts.isLoading && <LoadingBlock />}
            {contracts.data && contracts.data.length === 0 && (
              <p className="text-sm text-slate-500">Keine Verträge hinterlegt.</p>
            )}
            <ul className="divide-y divide-slate-100">
              {(contracts.data ?? []).map((contract) => (
                <li key={contract.id}>
                  <Link to={`/sponsoring/vertraege/${contract.id}`} className="flex flex-wrap items-center justify-between gap-2 py-2 hover:bg-slate-50">
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-slate-800">{contract.title}</span>
                      <span className="block text-xs text-slate-500">
                        {formatDate(contract.validFrom)} – {formatDate(contract.validUntil)} · {contract.organisationUnits.map((u) => u.name).join(', ')}
                      </span>
                    </span>
                    <span className="flex items-center gap-2">
                      {contract.detailsVisible && contract.amountBand && (
                        <Amount amount={contract.amount} band={contract.amountBand} currency={contract.currency} className="text-sm" />
                      )}
                      <Badge tone={contract.status === 'ACTIVE' ? 'success' : contract.status === 'EXPIRING' ? 'warning' : 'neutral'}>
                        {SPONSORSHIP_STATUS_LABELS[contract.status] ?? contract.status}
                      </Badge>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      {editOpen && <SponsorDialog sponsor={s} onClose={() => setEditOpen(false)} />}
      {requestOpen && <RequestDialog sponsorId={s.id} onClose={() => setRequestOpen(false)} />}
      <ContractDialog open={contractOpen} onClose={() => setContractOpen(false)} sponsors={[s]} />
    </div>
  );
}
